import { useEffect, useMemo } from 'react';
import { DemeterMarket } from '../../../../../Generated/Raven-Demeter';
import { useApplicationDispatch, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { selectValueMatrixTableInputsRequest, setValueMatrixTableInputsRequest } from '../../../../../Redux/Slices/ValueMatrixCalculatorSlice';
import Dropdown from '../../../../Components/Form/Inputs/Dropdown';
import LabelWithTooltip from '../../../../Components/Form/Inputs/LabelWithTooltip';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import styles from '../ValueMatrixCalculator.module.scss';
import { ValueMatrixStripType } from '../ValueMatrixDefinitions';

const minimumNumberOfContractMonthsForTwelveMonthStrip = 2;

const ValueMatrixStripTypeDropdown: React.FC = () => {
    const [translations] = useLanguage();
    const dispatch = useApplicationDispatch();
    const valueMatrixTableInputs = useApplicationSelector(selectValueMatrixTableInputsRequest);

    const stripTypeOptions = useMemo(() => {
        if (!valueMatrixTableInputs.product) {
            return [];
        }

        const options = [
            {
                label: translations.calculators.valueMatrix.text.contract1,
                value: 'firstContractMonth' as ValueMatrixStripType,
            },
            {
                label: translations.calculators.valueMatrix.text.contract2,
                value: 'secondContractMonth' as ValueMatrixStripType,
            },
        ];

        if (valueMatrixTableInputs.market === DemeterMarket.Energy) {
            options.push({
                label: translations.calculators.valueMatrix.text.forwardContract,
                value: 'forwardContract' as ValueMatrixStripType,
            });
        }

        if ((valueMatrixTableInputs.product.contractMonths ?? []).length >= minimumNumberOfContractMonthsForTwelveMonthStrip) {
            options.push({
                label: translations.calculators.valueMatrix.text.twelveMonthStrip,
                value: 'twelveMonthStrip' as ValueMatrixStripType,
            });
        }

        // Per discussions, for now this will only be availbale for dairy markets.
        if (valueMatrixTableInputs.market === DemeterMarket.Dairy) {
            options.unshift({
                label: translations.words.spot,
                value: 'spot' as ValueMatrixStripType,
            });
        }

        return options;
    }, [translations, valueMatrixTableInputs?.product]);

    // If we change to a product that doesn't have enough contracts for the currently selected strip type, we change it.
    useEffect(() => {
        if (stripTypeOptions.length === 0 || stripTypeOptions.map((x) => x.value).includes(valueMatrixTableInputs.stripType)) {
            if (valueMatrixTableInputs?.isCascadeUpdating) {
                dispatch(setValueMatrixTableInputsRequest({ ...valueMatrixTableInputs, isCascadeUpdating: false }));
            }

            return;
        }

        dispatch(
            setValueMatrixTableInputsRequest({
                ...valueMatrixTableInputs,
                stripType: stripTypeOptions[0].value,
                isCascadeUpdating: false,
            }),
        );
    }, [stripTypeOptions]);

    return (
        <div className={styles.value_matrix_dropdown}>
            <Dropdown
                testId="ValueMatrixStripTypeDropdown"
                value={valueMatrixTableInputs.stripType}
                options={stripTypeOptions}
                handleOptionChange={(value) => {
                    dispatch(
                        setValueMatrixTableInputsRequest({
                            ...valueMatrixTableInputs,
                            stripType: value as ValueMatrixStripType,
                            updateTriggeredBy: 'stripTypeSelection',
                        }),
                    );
                }}
                label={
                    <LabelWithTooltip
                        title={translations.calculators.valueMatrix.fields.stripType}
                        tooltip={
                            <>
                                <p>{translations.calculators.valueMatrix.text.stripTypeTooltipInformationFirstContract}</p>
                                <p>{translations.calculators.valueMatrix.text.stripTypeTooltipInformationSecondContract}</p>
                                <p>{translations.calculators.valueMatrix.text.stripTypeTooltipInformationForwardContract}</p>
                                <p>{translations.calculators.valueMatrix.text.stripTypeTooltipInformationTwelveMonth}</p>
                            </>
                        }
                    />
                }
                track={{ eventCategory: 'ValueMatrixCalculator', eventAction: 'StripTypeChanged', eventValue: valueMatrixTableInputs.stripType }}
            />
        </div>
    );
};

export default ValueMatrixStripTypeDropdown;
